/**
 * MessageInput Component
 * Input field for composing and sending messages
 */

import React, { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { Input, Button } from 'antd';
import { SendOutlined, PaperClipOutlined } from '@ant-design/icons';
import './MessageInput.css';

const { TextArea } = Input;

interface MessageInputProps {
  /** Callback when message is sent */
  onSendMessage: (message: string) => void;
  /** Disable input */
  disabled?: boolean;
  /** Show loading state */
  loading?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Auto-focus on mount */
  autoFocus?: boolean;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled = false,
  loading = false,
  placeholder = 'Type a message...',
  autoFocus = false,
}) => {
  const [message, setMessage] = useState('');
  const textAreaRef = useRef<any>(null);

  // Auto-focus on mount if requested
  useEffect(() => {
    if (autoFocus && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [autoFocus]);

  // Handle send message
  const handleSend = () => {
    if (!message.trim() || disabled || loading) return;

    onSendMessage(message.trim());
    setMessage('');

    // Focus back to input
    setTimeout(() => {
      textAreaRef.current?.focus();
    }, 100);
  };

  // Handle Enter key (send message)
  // Shift+Enter for new line
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="message-input-container">
      <div className="message-input-wrapper">
        {/* Attachment button (future phase) */}
        <Button
          className="message-input-attachment"
          icon={<PaperClipOutlined />}
          type="text"
          disabled={disabled}
          title="Attach file (coming soon)"
        />

        {/* Message textarea */}
        <TextArea
          ref={textAreaRef}
          className="message-input-textarea"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || loading}
          autoSize={{ minRows: 1, maxRows: 5 }}
          maxLength={4000}
        />

        {/* Send button */}
        <Button
          className="message-input-send"
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          disabled={!message.trim() || disabled}
          loading={loading}
          title="Send message (Enter)"
        >
          Send
        </Button>
      </div>

      {/* Character count (optional) */}
      {message.length > 3500 && (
        <div className="message-input-counter">{message.length} / 4000</div>
      )}
    </div>
  );
};
